/** GST amount embedded in an inclusive price. */
export function gstFromInclusive(amount, ratePercent) {
  const amountNum = Number(amount) || 0;
  const rate = Number(ratePercent) || 0;
  if (amountNum <= 0 || rate <= 0) return 0;
  return amountNum * (rate / (100 + rate));
}

export function taxableFromInclusive(amount, ratePercent) {
  const amountNum = Number(amount) || 0;
  return amountNum - gstFromInclusive(amountNum, ratePercent);
}

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

export function isInterStateSupply(storeState, customerState) {
  const store = String(storeState || "").trim().toLowerCase();
  const customer = String(customerState || "").trim().toLowerCase();
  if (!store || !customer) return false;
  return store !== customer;
}

export function splitGstAmount(taxAmount, isInterState) {
  const amt = round2(taxAmount);
  if (amt <= 0) return { cgst: 0, sgst: 0, igst: 0 };
  if (isInterState) return { cgst: 0, sgst: 0, igst: amt };
  const half = round2(amt / 2);
  return { cgst: half, sgst: round2(amt - half), igst: 0 };
}

/** GST for a single cart line, using the product's gst_percent. */
export function calcLineGst(item, priceIncludesGst, storeState, customerState) {
  const lineAmount = (Number(item.unit_price) || 0) * (Number(item.quantity) || 0) - (Number(item.discount) || 0);
  const rate = Number(item.gst_percent ?? 5) || 0;
  const amount = priceIncludesGst
    ? round2(gstFromInclusive(lineAmount, rate))
    : round2(lineAmount * (rate / 100));
  const isInterState = isInterStateSupply(storeState, customerState);
  const split = splitGstAmount(amount, isInterState);
  return { rate, amount, isInterState, ...split };
}

/** Compute POS bill totals for exclusive or inclusive store pricing. */
export function calcPosBill(items, billDiscount, priceIncludesGst, storeState, customerState) {
  const discount = Number(billDiscount) || 0;
  const defaultRate = 5;

  const lines = items.map((it) => {
    const lineAmount = (Number(it.unit_price) || 0) * (Number(it.quantity) || 0) - (Number(it.discount) || 0);
    const rate = Number(it.gst_percent ?? defaultRate) || 0;

    if (priceIncludesGst) {
      const gst = gstFromInclusive(lineAmount, rate);
      return { inclusive: lineAmount, taxable: lineAmount - gst, gst, rate };
    }

    const taxable = lineAmount;
    const gst = taxable * (rate / 100);
    return { inclusive: taxable + gst, taxable, gst, rate };
  });

  const grossInclusive = lines.reduce((sum, line) => sum + line.inclusive, 0);
  const grossTaxable = lines.reduce((sum, line) => sum + line.taxable, 0);
  const grossGst = lines.reduce((sum, line) => sum + line.gst, 0);

  const grossTotal = round2(grossInclusive);
  const isInterState = isInterStateSupply(storeState, customerState);

  if (priceIncludesGst) {
    const netInclusive = Math.max(0, grossInclusive - discount);
    const ratio = grossInclusive > 0 ? netInclusive / grossInclusive : 1;
    const subtotal = round2(grossTaxable * ratio);
    const taxAmt = round2(grossGst * ratio);
    const total = round2(netInclusive);
    const effectiveTaxPercent = subtotal > 0 ? round2((taxAmt / subtotal) * 100) : 0;
    const { cgst, sgst, igst } = splitGstAmount(taxAmt, isInterState);
    return { grossTotal, subtotal, taxAmt, total, taxPercent: effectiveTaxPercent, priceIncludesGst: true, cgst, sgst, igst, isInterState };
  }

  const subtotal = round2(grossTaxable);
  const taxAmt = round2(grossGst);
  const total = round2(Math.max(0, grossInclusive - discount));
  const effectiveTaxPercent = subtotal > 0 ? round2((taxAmt / subtotal) * 100) : 0;
  const { cgst, sgst, igst } = splitGstAmount(taxAmt, isInterState);
  return { grossTotal, subtotal, taxAmt, total, taxPercent: effectiveTaxPercent, priceIncludesGst: false, cgst, sgst, igst, isInterState };
}
